// PAR Intelligence "agents" — background specialists that continuously scan the
// data and surface insights. Each agent owns a slice of the insight catalogue and
// carries a consistent identity (name, accent, glyph) that follows the user from
// the insight card, through the action workflow, and into the chat drawer.

import {AGENT_TO_COACH, CATEGORY_TO_PRODUCTS, COACH_BY_ID} from "./coachCatalog";
import {insightCategories, type InsightCard} from "./insightCardModels";
import loyaltyIqAvatar from "@/img/loyalty-iq.png";
import restaurantCoreAvatar from "@/img/PAR-Restaurant-Logo-Positive.png";
import plAdvisorAvatar from "@/img/pl-advisor.png";
import guestPulseAvatar from "@/img/guest-pulse.png";
import opsPulseAvatar from "@/img/opspulse.png";
import lossIntelAvatar from "@/img/loss-intel.png";

/**
 * The lightweight identity that travels with every surface an agent touches.
 * Kept intentionally small so it can be embedded in workflows and chat sessions.
 */
export interface AgentIdentity {
    id: string;
    name: string;
    /** Short domain tagline, e.g. "Loyalty · Campaigns · LTV". */
    specialty: string;
    /** Primary accent colour (hex) used to theme every surface this agent owns. */
    accent: string;
    /** Circular portrait (imported PNG) shown on every surface this agent owns. */
    avatar: string;
}

/** 0–100 competency bars shown on the agent detail rail. */
export interface AgentCapabilities {
    speed: number;
    precision: number;
    coverage: number;
    intelligence: number;
}

/** An "agentic task" the agent can be told to perform (Mission tab checkboxes). */
export interface AgentTask {
    id: string;
    label: string;
    defaultOn: boolean;
}

/** A budget target the agent watches against (Performance tab, % of sales). */
export interface AgentBudgetTarget {
    id: string;
    label: string;
    value: number;
    unit: string;
}

/** A tunable KPI range the agent enforces (Performance tab dual sliders). */
export interface AgentKpiThreshold {
    id: string;
    label: string;
    min: number;
    max: number;
    unit: string;
    /** Absolute slider bounds. */
    floor: number;
    ceiling: number;
    step: number;
}

/** A toggleable data source the agent can read from (Connections tab). */
export interface AgentDataSource {
    id: string;
    label: string;
    description: string;
    defaultOn: boolean;
}

/** A third-party platform the agent can integrate with (Connections tab). */
export interface AgentIntegration {
    id: string;
    name: string;
    category: string;
    description: string;
    /** Short badge shown in the integration row. */
    badge: string;
    connected: boolean;
}

/** Accent for an agent, inherited from the Coach it rolls up to (AGENT_TO_COACH). */
const coachAccent = (agentId: string): string => {
    const coachId = AGENT_TO_COACH[agentId];
    return (coachId && COACH_BY_ID[coachId]?.accent) ?? "#8c9fff";
};

export interface InsightAgent extends AgentIdentity {
    /** One-line description shown on the agent roster card. */
    description: string;
    /** Playful competency badge, e.g. "S", "A+". */
    rank: string;
    /** What the agent is "currently scanning" — shown as a live status line. */
    scanLine: string;
    /** Insight category ids this agent is responsible for. */
    categoryIds: string[];
    /** Whether the agent is deployed. Disabled agents surface no insights. */
    enabled: boolean;
    /** Playful persona name shown on the detail rail, e.g. "THE LOYALIST". */
    codename: string;
    /** The agent's editable mission statement. */
    mission: string;
    /** Competency bars for the detail rail. */
    capabilities: AgentCapabilities;
    /** Agentic tasks the agent can be assigned. */
    tasks: AgentTask[];
    /** Budget targets the agent watches. */
    budgetTargets: AgentBudgetTarget[];
    /** KPI ranges the agent enforces. */
    kpiThresholds: AgentKpiThreshold[];
    /** Data sources the agent can read from. */
    dataSources: AgentDataSource[];
    /** Third-party integrations the agent can connect. */
    integrations: AgentIntegration[];
}

export const insightAgents: InsightAgent[] = [
    {
        id: "agent_loyalty_iq",
        name: "Loyalty Coach",
        specialty: "Loyalty · Retention · Churn",
        accent: coachAccent("agent_loyalty_iq"),
        avatar: loyaltyIqAvatar,
        description:
            "Watches enrollment, redemption, and member lifetime value — then drafts the retention plays to protect them.",
        rank: "S",
        scanLine:
            "Scanning enrollment, redemption & churn signals across 42 locations",
        categoryIds: ["punchh", "loyalty_churn"],
        enabled: true,
        codename: "THE LOYALIST",
        mission:
            "Maximize the return on every loyalty dollar by surfacing offer performance, churn signals, and member behavior trends before they cost revenue.",
        capabilities: {speed: 85, precision: 90, coverage: 78, intelligence: 92},
        tasks: [
            {
                id: "task_offer_redemption",
                label: "Monitor weekly offer redemption rates across all locations",
                defaultOn: true
            },
            {
                id: "task_flag_campaigns",
                label: "Flag campaigns with redemption below threshold",
                defaultOn: true
            },
            {
                id: "task_at_risk",
                label: "Surface top 10% at-risk members for re-engagement",
                defaultOn: true
            },
            {
                id: "task_lto_baseline",
                label: "Compare LTO performance vs. historical baseline",
                defaultOn: false
            },
            {
                id: "task_high_ltv",
                label: "Identify high-LTV member segments",
                defaultOn: false
            },
            {
                id: "task_weekly_summary",
                label: "Weekly loyalty program health summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_offer",
                label: "Loyalty Offer Budget",
                value: 3.5,
                unit: "% of sales"
            },
            {
                id: "budget_campaign",
                label: "Campaign Spend",
                value: 1.2,
                unit: "% of sales"
            },
            {
                id: "budget_recovery",
                label: "Guest Recovery Budget",
                value: 0.4,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_visit_freq",
                label: "Visit Frequency",
                min: 1.5,
                max: 3.5,
                unit: "visits/mo",
                floor: 0,
                ceiling: 6,
                step: 0.1
            },
            {
                id: "kpi_redemption",
                label: "Redemption Rate",
                min: 18,
                max: 40,
                unit: "%",
                floor: 0,
                ceiling: 60,
                step: 1
            },
            {
                id: "kpi_retention",
                label: "Member Retention",
                min: 60,
                max: 88,
                unit: "%",
                floor: 0,
                ceiling: 100,
                step: 1
            },
            {
                id: "kpi_incrementality",
                label: "Offer Incrementality",
                min: 12,
                max: 35,
                unit: "%",
                floor: 0,
                ceiling: 50,
                step: 1
            }
        ],
        dataSources: [
            {
                id: "ds_punchh",
                label: "PAR Punchh",
                description: "Loyalty & member data",
                defaultOn: true
            },
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Transaction-level data",
                defaultOn: true
            },
            {
                id: "ds_campaign",
                label: "Campaign History",
                description: "Historical offer data",
                defaultOn: false
            },
            {
                id: "ds_demographics",
                label: "Member Demographics",
                description: "Guest profile data",
                defaultOn: false
            },
            {
                id: "ds_email",
                label: "Email / SMS Platform",
                description: "Communication engagement",
                defaultOn: false
            },
            {
                id: "ds_app",
                label: "App Behavior",
                description: "Mobile ordering data",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_punchh",
                name: "PAR Punchh",
                category: "Loyalty Platform",
                description: "Member data, points, offers, and redemption history",
                badge: "PNCH",
                connected: true
            },
            {
                id: "int_paytronix",
                name: "Paytronix",
                category: "Loyalty & Payments",
                description: "Loyalty programs, gift cards, and mobile ordering",
                badge: "PYTX",
                connected: false
            },
            {
                id: "int_olo",
                name: "Olo",
                category: "Online Ordering",
                description: "Digital ordering data and guest behavior",
                badge: "OLO",
                connected: false
            },
            {
                id: "int_klaviyo",
                name: "Klaviyo",
                category: "Email & SMS",
                description: "Campaign engagement, opens, and conversion tracking",
                badge: "KLVY",
                connected: false
            },
            {
                id: "int_fishbowl",
                name: "Fishbowl",
                category: "Restaurant CRM",
                description:
                    "Guest database, email marketing, and loyalty analytics",
                badge: "FISH",
                connected: false
            }
        ]
    },
    {
        id: "agent_pl_advisor",
        name: "Inventory Coach",
        specialty: "Food Cost · Waste · Variance",
        accent: coachAccent("agent_pl_advisor"),
        avatar: plAdvisorAvatar,
        description:
            "Reconciles theoretical vs actual food cost line by line and surfaces where margin is leaking.",
        rank: "A+",
        scanLine: "Reconciling theoretical vs actual food cost item by item",
        categoryIds: ["cogs"],
        enabled: true,
        codename: "THE ACCOUNTANT",
        mission:
            "Protect margin on every plate by reconciling theoretical vs actual cost line by line and flagging the moment food or labor spend drifts past target.",
        capabilities: {speed: 74, precision: 95, coverage: 82, intelligence: 88},
        tasks: [
            {
                id: "task_cogs_recon",
                label: "Reconcile theoretical vs actual food cost daily",
                defaultOn: true
            },
            {
                id: "task_variance_flag",
                label: "Flag items with variance above threshold",
                defaultOn: true
            },
            {
                id: "task_waste_track",
                label: "Track waste and over-portioning by location",
                defaultOn: true
            },
            {
                id: "task_vendor_price",
                label: "Watch vendor price changes vs contract",
                defaultOn: false
            },
            {
                id: "task_margin_forecast",
                label: "Forecast margin impact of menu price changes",
                defaultOn: false
            },
            {
                id: "task_weekly_pl",
                label: "Weekly P&L variance summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_food",
                label: "Food Cost Target",
                value: 28.0,
                unit: "% of sales"
            },
            {
                id: "budget_labor",
                label: "Labor Cost Target",
                value: 24.0,
                unit: "% of sales"
            },
            {
                id: "budget_waste",
                label: "Waste Allowance",
                value: 1.5,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_food_variance",
                label: "Food Cost Variance",
                min: -2,
                max: 2,
                unit: "%",
                floor: -6,
                ceiling: 6,
                step: 0.5
            },
            {
                id: "kpi_prime_cost",
                label: "Prime Cost",
                min: 52,
                max: 60,
                unit: "%",
                floor: 40,
                ceiling: 75,
                step: 1
            },
            {
                id: "kpi_waste",
                label: "Waste Rate",
                min: 0,
                max: 2.5,
                unit: "%",
                floor: 0,
                ceiling: 8,
                step: 0.1
            },
            {
                id: "kpi_theo_actual",
                label: "Theo-to-Actual Gap",
                min: 0,
                max: 3,
                unit: "%",
                floor: 0,
                ceiling: 10,
                step: 0.5
            }
        ],
        dataSources: [
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Item-level sales mix",
                defaultOn: true
            },
            {
                id: "ds_inventory",
                label: "Inventory Counts",
                description: "Actual usage & on-hand",
                defaultOn: true
            },
            {
                id: "ds_recipes",
                label: "Recipe / BOM",
                description: "Theoretical cost per item",
                defaultOn: true
            },
            {
                id: "ds_invoices",
                label: "Vendor Invoices",
                description: "Purchase price data",
                defaultOn: false
            },
            {
                id: "ds_labor",
                label: "Labor / Scheduling",
                description: "Hours & wage data",
                defaultOn: false
            },
            {
                id: "ds_gl",
                label: "General Ledger",
                description: "Accounting actuals",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_restaurant365",
                name: "Restaurant365",
                category: "Accounting & Ops",
                description: "Inventory, invoicing, and P&L reconciliation",
                badge: "R365",
                connected: true
            },
            {
                id: "int_marketman",
                name: "MarketMan",
                category: "Inventory",
                description: "Purchasing, inventory, and recipe costing",
                badge: "MMAN",
                connected: false
            },
            {
                id: "int_compeat",
                name: "Compeat",
                category: "Back Office",
                description: "Accounting, inventory, and labor management",
                badge: "CMPT",
                connected: false
            },
            {
                id: "int_sysco",
                name: "Sysco",
                category: "Distributor",
                description: "Vendor catalog and invoice pricing feed",
                badge: "SYSCO",
                connected: false
            },
            {
                id: "int_quickbooks",
                name: "QuickBooks",
                category: "Accounting",
                description: "General ledger and financial actuals",
                badge: "QB",
                connected: false
            }
        ]
    },
    {
        id: "agent_menu_core",
        name: "Restaurant Core",
        specialty: "Menu · Mix · Profitability",
        accent: coachAccent("agent_menu_core"),
        avatar: restaurantCoreAvatar,
        description:
            "Tracks menu profitability and item-level variance so core menu economics stay visible.",
        rank: "A",
        scanLine: "Reviewing menu economics and item-level profitability",
        categoryIds: ["menu"],
        enabled: true,
        codename: "THE MENU SCIENTIST",
        mission:
            "Protect menu profitability by surfacing item-level variance, theoretical-vs-actual gaps, and the menu actions that move margin fastest.",
        capabilities: {speed: 70, precision: 91, coverage: 74, intelligence: 87},
        tasks: [
            {
                id: "task_menu_profit",
                label: "Rank menu items by profitability and variance",
                defaultOn: true
            },
            {
                id: "task_recipe_gap",
                label: "Flag items with actual usage above recipe standard",
                defaultOn: true
            },
            {
                id: "task_mix_shift",
                label: "Model menu mix shifts against margin impact",
                defaultOn: true
            },
            {
                id: "task_price_change",
                label: "Forecast menu price change impact",
                defaultOn: false
            },
            {
                id: "task_item_lift",
                label: "Highlight items with the highest margin recovery",
                defaultOn: false
            },
            {
                id: "task_weekly_menu",
                label: "Weekly menu economics summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_menu",
                label: "Menu Margin Target",
                value: 32.0,
                unit: "% gross margin"
            },
            {
                id: "budget_variance",
                label: "Variance Tolerance",
                value: 2.5,
                unit: "% of theoretical"
            },
            {
                id: "budget_recovery",
                label: "Recoverable Margin",
                value: 0.7,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_food_cost",
                label: "Food Cost %",
                min: 26,
                max: 31,
                unit: "%",
                floor: 20,
                ceiling: 40,
                step: 0.1
            },
            {
                id: "kpi_variance",
                label: "Variance %",
                min: 0.8,
                max: 2.5,
                unit: "%",
                floor: 0,
                ceiling: 8,
                step: 0.1
            },
            {
                id: "kpi_margin",
                label: "Menu Gross Margin",
                min: 30,
                max: 36,
                unit: "%",
                floor: 20,
                ceiling: 45,
                step: 0.1
            },
            {
                id: "kpi_item_recovery",
                label: "Item Recovery Rate",
                min: 50,
                max: 80,
                unit: "%",
                floor: 0,
                ceiling: 100,
                step: 1
            }
        ],
        dataSources: [
            {
                id: "ds_menu",
                label: "Menu / Recipe Data",
                description: "Recipes, item standards, and menu mix",
                defaultOn: true
            },
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Item-level usage and sales",
                defaultOn: true
            },
            {
                id: "ds_cost",
                label: "Ingredient Cost",
                description: "Purchase and theoretical cost",
                defaultOn: true
            },
            {
                id: "ds_labor",
                label: "Labor / Scheduling",
                description: "Labor context for menu execution",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_recipe",
                name: "Recipe Manager",
                category: "Menu Ops",
                description: "Recipe standards and theoretical cost",
                badge: "RCPE",
                connected: true
            },
            {
                id: "int_backoffice",
                name: "Back Office",
                category: "Profitability",
                description: "Menu item cost and margin reporting",
                badge: "BKO",
                connected: false
            },
            {
                id: "int_inventory",
                name: "Inventory System",
                category: "Inventory",
                description: "Item usage, variance, and ordering",
                badge: "INV",
                connected: false
            }
        ]
    },
    {
        id: "agent_guest_pulse",
        name: "Guest Pulse",
        specialty: "CSAT · NPS · Satisfaction",
        accent: coachAccent("agent_guest_pulse"),
        avatar: guestPulseAvatar,
        description:
            "Tracks guest satisfaction, NPS, and complaint trends so you fix the experience before it costs a visit.",
        rank: "A",
        scanLine: "Listening to CSAT, NPS & guest sentiment across every store",
        categoryIds: ["labor"],
        enabled: true,
        codename: "THE LISTENER",
        mission:
            "Defend every guest visit by tracking satisfaction, NPS, and complaint trends so the experience gets fixed before it costs a return trip.",
        capabilities: {speed: 88, precision: 80, coverage: 90, intelligence: 84},
        tasks: [
            {
                id: "task_csat_monitor",
                label: "Monitor CSAT and NPS across all locations",
                defaultOn: true
            },
            {
                id: "task_complaint_spike",
                label: "Flag complaint spikes by store and daypart",
                defaultOn: true
            },
            {
                id: "task_sentiment_theme",
                label: "Surface emerging themes from guest feedback",
                defaultOn: true
            },
            {
                id: "task_recovery",
                label: "Recommend service-recovery offers for detractors",
                defaultOn: false
            },
            {
                id: "task_review_reply",
                label: "Draft replies to public reviews",
                defaultOn: false
            },
            {
                id: "task_weekly_voice",
                label: "Weekly voice-of-guest summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_recovery",
                label: "Service Recovery Budget",
                value: 0.6,
                unit: "% of sales"
            },
            {
                id: "budget_survey",
                label: "Survey Incentive Spend",
                value: 0.2,
                unit: "% of sales"
            },
            {
                id: "budget_makegood",
                label: "Make-Good Allowance",
                value: 0.5,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_csat",
                label: "CSAT Score",
                min: 82,
                max: 95,
                unit: "%",
                floor: 50,
                ceiling: 100,
                step: 1
            },
            {
                id: "kpi_nps",
                label: "Net Promoter Score",
                min: 40,
                max: 70,
                unit: "",
                floor: -20,
                ceiling: 100,
                step: 1
            },
            {
                id: "kpi_response",
                label: "Survey Response Rate",
                min: 8,
                max: 25,
                unit: "%",
                floor: 0,
                ceiling: 40,
                step: 1
            },
            {
                id: "kpi_resolution",
                label: "Complaint Resolution Time",
                min: 2,
                max: 24,
                unit: "hrs",
                floor: 0,
                ceiling: 72,
                step: 1
            }
        ],
        dataSources: [
            {
                id: "ds_survey",
                label: "Guest Surveys",
                description: "CSAT & NPS responses",
                defaultOn: true
            },
            {
                id: "ds_reviews",
                label: "Public Reviews",
                description: "Google, Yelp, app stores",
                defaultOn: true
            },
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Visit & order context",
                defaultOn: false
            },
            {
                id: "ds_social",
                label: "Social Mentions",
                description: "Brand social listening",
                defaultOn: false
            },
            {
                id: "ds_complaints",
                label: "Complaint Log",
                description: "Guest care tickets",
                defaultOn: false
            },
            {
                id: "ds_loyalty",
                label: "Loyalty Profiles",
                description: "Member sentiment linkage",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_qualtrics",
                name: "Qualtrics",
                category: "Experience Mgmt",
                description: "Survey distribution and CSAT/NPS analytics",
                badge: "QLTX",
                connected: true
            },
            {
                id: "int_medallia",
                name: "Medallia",
                category: "Experience Mgmt",
                description: "Real-time guest feedback and sentiment",
                badge: "MDLA",
                connected: false
            },
            {
                id: "int_yelp",
                name: "Yelp",
                category: "Reviews",
                description: "Public review stream and ratings",
                badge: "YELP",
                connected: false
            },
            {
                id: "int_google",
                name: "Google Business",
                category: "Reviews",
                description: "Location reviews and ratings feed",
                badge: "GBP",
                connected: false
            },
            {
                id: "int_sprinklr",
                name: "Sprinklr",
                category: "Social Listening",
                description: "Social mentions and brand sentiment",
                badge: "SPKR",
                connected: false
            }
        ]
    },
    {
        id: "agent_ops_pulse",
        name: "Drive-Thru Coach",
        specialty: "Speed of Service · Throughput · Execution",
        accent: coachAccent("agent_ops_pulse"),
        avatar: opsPulseAvatar,
        description:
            "Watches drive-thru speed of service in real time and flags the moment a location slips past target.",
        rank: "S+",
        scanLine: "Watching drive-thru speed of service in real time",
        categoryIds: ["drivethru_sos"],
        enabled: true,
        codename: "THE CONDUCTOR",
        mission:
            "Keep every line moving by watching drive-thru speed of service in real time and flagging the moment a location slips past target.",
        capabilities: {speed: 96, precision: 86, coverage: 80, intelligence: 83},
        tasks: [
            {
                id: "task_sos_realtime",
                label: "Watch drive-thru speed of service in real time",
                defaultOn: true
            },
            {
                id: "task_sos_breach",
                label: "Flag locations breaching SOS target by daypart",
                defaultOn: true
            },
            {
                id: "task_bottleneck",
                label: "Pinpoint the slow station (speaker, queue, service)",
                defaultOn: true
            },
            {
                id: "task_staffing",
                label: "Recommend staffing shifts for peak windows",
                defaultOn: false
            },
            {
                id: "task_throughput",
                label: "Track car counts and throughput vs forecast",
                defaultOn: false
            },
            {
                id: "task_weekly_sos",
                label: "Weekly speed-of-service summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_labor",
                label: "Peak Labor Budget",
                value: 22.0,
                unit: "% of sales"
            },
            {
                id: "budget_overtime",
                label: "Overtime Allowance",
                value: 1.0,
                unit: "% of sales"
            },
            {
                id: "budget_bonus",
                label: "Speed Bonus Pool",
                value: 0.3,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_total_sos",
                label: "Total SOS",
                min: 180,
                max: 240,
                unit: "sec",
                floor: 120,
                ceiling: 360,
                step: 5
            },
            {
                id: "kpi_speaker",
                label: "Speaker Time",
                min: 30,
                max: 60,
                unit: "sec",
                floor: 0,
                ceiling: 120,
                step: 5
            },
            {
                id: "kpi_queue",
                label: "Queue Time",
                min: 60,
                max: 120,
                unit: "sec",
                floor: 0,
                ceiling: 240,
                step: 5
            },
            {
                id: "kpi_service",
                label: "Service Time",
                min: 45,
                max: 90,
                unit: "sec",
                floor: 0,
                ceiling: 180,
                step: 5
            }
        ],
        dataSources: [
            {
                id: "ds_timer",
                label: "Drive-Thru Timer",
                description: "Station-level SOS data",
                defaultOn: true
            },
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Order & throughput data",
                defaultOn: true
            },
            {
                id: "ds_labor",
                label: "Labor / Scheduling",
                description: "Staffing by daypart",
                defaultOn: false
            },
            {
                id: "ds_kds",
                label: "Kitchen Display",
                description: "Prep & make times",
                defaultOn: false
            },
            {
                id: "ds_weather",
                label: "Weather Feed",
                description: "Demand context",
                defaultOn: false
            },
            {
                id: "ds_forecast",
                label: "Sales Forecast",
                description: "Expected volume",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_hme",
                name: "HME Zoom",
                category: "Drive-Thru Timer",
                description: "Station-level speed-of-service timing data",
                badge: "HME",
                connected: true
            },
            {
                id: "int_dtechnologies",
                name: "Delphi Display",
                category: "Drive-Thru Timer",
                description: "Drive-thru timing and digital menu boards",
                badge: "DLPH",
                connected: false
            },
            {
                id: "int_qtrac",
                name: "Qtrac",
                category: "Queue Mgmt",
                description: "Line management and wait-time analytics",
                badge: "QTRC",
                connected: false
            },
            {
                id: "int_kds",
                name: "QSR Automations",
                category: "Kitchen Ops",
                description: "Kitchen display and production timing",
                badge: "QSR",
                connected: false
            },
            {
                id: "int_hotschedules",
                name: "HotSchedules",
                category: "Labor",
                description: "Scheduling and labor forecasting",
                badge: "HOT",
                connected: false
            }
        ]
    },
    {
        id: "agent_loss_intel",
        name: "Fraud Coach",
        specialty: "Shrink · Exceptions · Integrity",
        accent: coachAccent("agent_loss_intel"),
        avatar: lossIntelAvatar,
        description:
            "Sweeps voids, refunds, and discount exceptions to catch shrink and integrity issues before they add up.",
        rank: "S",
        scanLine: "Sweeping voids, discounts & exceptions for shrink signals",
        categoryIds: ["sell"],
        enabled: true,
        codename: "THE SENTINEL",
        mission:
            "Guard the bottom line by sweeping voids, refunds, and discount exceptions to catch shrink and integrity issues before they add up.",
        capabilities: {speed: 82, precision: 93, coverage: 88, intelligence: 90},
        tasks: [
            {
                id: "task_void_sweep",
                label: "Sweep voids and refunds for anomalies",
                defaultOn: true
            },
            {
                id: "task_discount_abuse",
                label: "Flag discount and comp abuse by employee",
                defaultOn: true
            },
            {
                id: "task_cash_variance",
                label: "Track cash drawer variance by shift",
                defaultOn: true
            },
            {
                id: "task_no_sale",
                label: "Watch no-sale and drawer-open events",
                defaultOn: false
            },
            {
                id: "task_inventory_shrink",
                label: "Correlate inventory shrink with exceptions",
                defaultOn: false
            },
            {
                id: "task_weekly_loss",
                label: "Weekly loss-prevention summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_shrink",
                label: "Acceptable Shrink",
                value: 1.0,
                unit: "% of sales"
            },
            {
                id: "budget_comp",
                label: "Comp / Discount Budget",
                value: 2.0,
                unit: "% of sales"
            },
            {
                id: "budget_void",
                label: "Void Allowance",
                value: 0.8,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_void_rate",
                label: "Void Rate",
                min: 0,
                max: 2,
                unit: "%",
                floor: 0,
                ceiling: 8,
                step: 0.1
            },
            {
                id: "kpi_comp_rate",
                label: "Comp Rate",
                min: 0,
                max: 3,
                unit: "%",
                floor: 0,
                ceiling: 10,
                step: 0.1
            },
            {
                id: "kpi_cash_variance",
                label: "Cash Variance",
                min: 0,
                max: 1,
                unit: "%",
                floor: 0,
                ceiling: 5,
                step: 0.1
            },
            {
                id: "kpi_shrink",
                label: "Inventory Shrink",
                min: 0,
                max: 1.5,
                unit: "%",
                floor: 0,
                ceiling: 6,
                step: 0.1
            }
        ],
        dataSources: [
            {
                id: "ds_pos",
                label: "POS Transactions",
                description: "Void, comp & refund logs",
                defaultOn: true
            },
            {
                id: "ds_drawer",
                label: "Cash Drawer",
                description: "Shift reconciliation data",
                defaultOn: true
            },
            {
                id: "ds_employee",
                label: "Employee Records",
                description: "Operator-level activity",
                defaultOn: false
            },
            {
                id: "ds_inventory",
                label: "Inventory Counts",
                description: "Shrink correlation",
                defaultOn: false
            },
            {
                id: "ds_cctv",
                label: "CCTV Events",
                description: "Exception video linkage",
                defaultOn: false
            },
            {
                id: "ds_scheduling",
                label: "Scheduling",
                description: "Shift & staffing context",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_ncr",
                name: "NCR Aloha",
                category: "POS",
                description: "Transaction, void, and comp exception data",
                badge: "NCR",
                connected: false
            },
            {
                id: "int_agilence",
                name: "Agilence",
                category: "Loss Prevention",
                description: "Exception-based reporting and analytics",
                badge: "AGIL",
                connected: false
            },
            {
                id: "int_dtiq",
                name: "DTiQ",
                category: "Video Analytics",
                description: "Video surveillance and exception matching",
                badge: "DTIQ",
                connected: false
            },
            {
                id: "int_solink",
                name: "Solink",
                category: "Video Analytics",
                description: "Cloud video linked to POS transactions",
                badge: "SLNK",
                connected: false
            },
            {
                id: "int_appetize",
                name: "Appetize",
                category: "POS",
                description: "Enterprise POS and payment exceptions",
                badge: "APTZ",
                connected: false
            }
        ]
    },
    {
        id: "agent_campaign_coach",
        name: "Campaign Coach",
        specialty: "Campaigns · Offers · ROI",
        accent: coachAccent("agent_campaign_coach"),
        // Reuses the Loyalty Coach portrait pending a dedicated Campaign Coach avatar asset.
        avatar: loyaltyIqAvatar,
        description:
            "Tracks offer redemption, audience targeting, and incrementality — then flags offers that are giving away margin without driving net-new visits.",
        rank: "A",
        scanLine:
            "Scanning redemption rate and incrementality across active offers",
        categoryIds: ["campaign_effectiveness"],
        enabled: true,
        codename: "THE STRATEGIST",
        mission:
            "Protect offer margin by making sure every campaign drives incremental visits instead of discounting guests who would have come anyway.",
        capabilities: {speed: 80, precision: 88, coverage: 75, intelligence: 90},
        tasks: [
            {
                id: "task_campaign_redemption",
                label: "Monitor redemption rate against the campaign's target threshold",
                defaultOn: true
            },
            {
                id: "task_campaign_incrementality",
                label: "Flag campaigns where existing loyalty members drive most redemptions",
                defaultOn: true
            },
            {
                id: "task_campaign_segment",
                label: "Recommend segment narrowing for low-incrementality offers",
                defaultOn: true
            },
            {
                id: "task_campaign_roi",
                label: "Track margin recovered from campaign adjustments",
                defaultOn: false
            },
            {
                id: "task_campaign_ab",
                label: "Compare A/B test results across active campaigns",
                defaultOn: false
            },
            {
                id: "task_campaign_summary",
                label: "Weekly campaign performance summary",
                defaultOn: false
            }
        ],
        budgetTargets: [
            {
                id: "budget_campaign_spend",
                label: "Campaign Spend",
                value: 1.2,
                unit: "% of sales"
            },
            {
                id: "budget_campaign_discount",
                label: "Offer Discount Budget",
                value: 2.0,
                unit: "% of sales"
            }
        ],
        kpiThresholds: [
            {
                id: "kpi_redemption_rate",
                label: "Redemption Rate",
                min: 5,
                max: 15,
                unit: "%",
                floor: 0,
                ceiling: 30,
                step: 1
            },
            {
                id: "kpi_campaign_incrementality",
                label: "Incrementality",
                min: 40,
                max: 70,
                unit: "%",
                floor: 0,
                ceiling: 100,
                step: 1
            },
            {
                id: "kpi_campaign_roi",
                label: "Campaign ROI",
                min: 2,
                max: 5,
                unit: "x",
                floor: 0,
                ceiling: 10,
                step: 0.1
            }
        ],
        dataSources: [
            {
                id: "ds_campaign_punchh",
                label: "PAR Punchh",
                description: "Campaign and offer redemption data",
                defaultOn: true
            },
            {
                id: "ds_campaign_pos",
                label: "POS Transactions",
                description: "Transaction-level data",
                defaultOn: true
            },
            {
                id: "ds_campaign_history",
                label: "Campaign History",
                description: "Historical offer performance",
                defaultOn: true
            },
            {
                id: "ds_campaign_email",
                label: "Email / SMS Platform",
                description: "Communication engagement",
                defaultOn: false
            }
        ],
        integrations: [
            {
                id: "int_campaign_punchh",
                name: "PAR Punchh",
                category: "Loyalty Platform",
                description: "Offer and campaign redemption history",
                badge: "PNCH",
                connected: true
            },
            {
                id: "int_campaign_klaviyo",
                name: "Klaviyo",
                category: "Email & SMS",
                description: "Campaign engagement, opens, and conversion tracking",
                badge: "KLVY",
                connected: false
            },
            {
                id: "int_campaign_olo",
                name: "Olo",
                category: "Online Ordering",
                description: "Digital ordering data and guest behavior",
                badge: "OLO",
                connected: false
            }
        ]
    }
];

export function getAgentCards(agent: InsightAgent): InsightCard[] {
    return agent.categoryIds.flatMap(
        (categoryId) =>
            insightCategories.find((category) => category.id === categoryId)
                ?.cards ?? []
    );
}

export function getAgentIcon(agent: AgentIdentity, insightId?: string) {
    const categoryId = insightId
        ? insightCategories.find((category) =>
              category.cards.some((card) => card.id === insightId)
          )?.id
        : insightAgents.find((insightAgent) => insightAgent.id === agent.id)
              ?.categoryIds[0];
    const productKey = categoryId
        ? CATEGORY_TO_PRODUCTS[categoryId]?.[0]
        : undefined;
    if (!productKey) return undefined;

    const [coachId, productId] = productKey.split(":");
    return COACH_BY_ID[coachId]?.products.find(
        (product) => product.id === productId
    )?.icon;
}

const CARD_ID_TO_AGENT: Map<string, InsightAgent> = (() => {
    const map = new Map<string, InsightAgent>();
    for (const agent of insightAgents) {
        for (const card of getAgentCards(agent)) {
            map.set(card.id, agent);
        }
    }
    return map;
})();

export function getAgentForCard(card: InsightCard): InsightAgent | undefined {
    return CARD_ID_TO_AGENT.get(card.id);
}

export function findAgentById(id: string): InsightAgent | undefined {
    return insightAgents.find((agent) => agent.id === id);
}

export function toAgentIdentity(agent: InsightAgent): AgentIdentity {
    return {
        id: agent.id,
        name: agent.name,
        specialty: agent.specialty,
        accent: agent.accent,
        avatar: agent.avatar
    };
}
